import { formatName } from '@pickleballinc/lib/format-name';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { z } from 'zod';

import { SponsorImage } from '@/components/molecules/main-header/types';
import { TeamLeagueDistrict } from '@/entities';
import { TournamentEvent } from '@/entities/ScheduleEventMatchups';
import { CurrencyFormat } from '@/entities/shared/lookups/CurrencyFormat';
import { Photo } from '@/entities/shared/user/Photo';
import { publicEnv } from '@/lib/env';
import { usePermissionStore } from '@/store/permission-store';
import { Options } from '@/utils/types';

/**
 * Gets maxLength from a Zod schema field using multiple fallback methods
 */
export function getZodMaxLength(schema: z.ZodTypeAny, fieldName: string): number | undefined {
	if (fieldName.includes('.')) {
		try {
			const [arrayField, , field] = fieldName.split('.');
			if (!arrayField || !field) {
				return undefined;
			}
			// Get the array schema
			const arraySchema = (schema as any).shape?.[arrayField];
			// Get the element type schema (schema for each array item)
			const elementSchema = arraySchema?.element;
			// Now get maxLength from the field in the element schema
			return getZodMaxLength(elementSchema, field);
		} catch (_error) {
			// Continue to other methods if array handling fails
		}
	}

	// Method 1: Direct path (fastest)
	try {
		const result = (schema as any)._def.schema._def.schema.shape[fieldName]._def.innerType.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 2: Double nested innerType
	try {
		const result = (schema as any).shape?.[fieldName]?._def?.innerType?._def?.innerType?.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 3: Direct from shape's _def (for simple string().max())
	try {
		const result = (schema as any).shape?.[fieldName]?._def?.checks?.find((check: any) => check.kind === 'max')?.value;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 4: For refined schemas
	try {
		const baseSchema = (schema as any)._def?.schema?._def?.schema;
		const result = baseSchema?.shape?.[fieldName]?._def?.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 5: For optional/nullable fields
	try {
		const field = (schema as any)._def?.schema?._def?.schema?.shape?.[fieldName];
		const result = field?._def?.innerType?._def?.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 6: Try through shape directly
	try {
		const result = (schema as any).shape?.[fieldName]?._def?.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 7: From schema._def.schema._def.shape
	try {
		const result = (schema as any)._def.schema._def.shape[fieldName]._def.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 8: Check in checks array at different levels
	try {
		const checks = (schema as any)._def.schema._def.shape[fieldName]._def.checks;
		const result = checks?.find((check: any) => check.kind === 'max')?.value;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 9: Try getting from innerType directly
	try {
		const result = (schema as any)._def.schema._def.shape[fieldName]._def.innerType._def.checks?.find(
			(check: any) => check.kind === 'max'
		)?.value;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 10: For deeply nested schemas
	try {
		const result = (schema as any)._def.schema._def.shape[fieldName]._def.innerType._def.innerType._def.checks?.find(
			(check: any) => check.kind === 'max'
		)?.value;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// Method 11: Most generic approach (last resort)
	try {
		let currentSchema = schema as any;
		while (currentSchema?._def?.schema) {
			currentSchema = currentSchema._def.schema;
		}
		const field = currentSchema?.shape?.[fieldName];
		let currentField = field;
		while (currentField?._def?.innerType) {
			currentField = currentField._def.innerType;
		}
		// Try getting from checks
		if (currentField?._def?.checks) {
			const maxCheck = currentField._def.checks.find((check: any) => check.kind === 'max');
			if (maxCheck?.value != null) return maxCheck.value;
		}
		// Try getting direct maxLength
		const result = currentField?._def?.maxLength;
		if (!!result && result != null) return result;
	} catch (_error) {
		// Continue
	}

	// If all methods fail, return undefined
	return undefined;
}

export function pluralize(amount: number, word: string, suffix = 's'): string {
	return amount === 1 ? word : word + suffix;
}

export const suffixOptions = [
	{ value: 'I', label: 'I' },
	{ value: 'II', label: 'II' },
	{ value: 'III', label: 'III' },
	{ value: 'IV', label: 'IV' },
	{ value: 'Jr.', label: 'Jr.' },
	{ value: 'Sr.', label: 'Sr.' }
];

export const getPlayerImageUrl = (photo: Photo | null | undefined) => {
	return (
		photo?.headerImage?.url ||
		photo?.profileImage?.url ||
		photo?.headshot?.url ||
		photo?.fullImage?.url ||
		photo?.featuredImage?.url ||
		photo?.overviewImage?.url ||
		photo?.actionUserImage?.url ||
		photo?.featuredUserImage?.url
	);
};

export const getProfileImageUrl = (profileImage?: string | null, width?: number, height?: number): string => {
	if (profileImage) {
		const imageUrl = getCDNURL(profileImage, width || 500, height || 500);
		return imageUrl;
	} else {
		const defaultImageUrl = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/legacy/profile-image.svg`;
		return getCDNURL(defaultImageUrl, width || 500, height || 500);
	}
};

export const determinePriceDisplay = (price: number, currencyFormat?: CurrencyFormat): string => {
	if (!currencyFormat) {
		return price ? price.toFixed(2).toString() : '0.00';
	}
	if (currencyFormat.position === 'prefix') {
		return `${currencyFormat.symbol}${price ? price.toFixed(2).toString() : '0.00'}`;
	} else {
		return `${price ? price.toFixed(2).toString() : '0.00'}${currencyFormat.symbol}`;
	}
};

export const isEmptyObject = (obj: object): boolean => {
	return Object.keys(obj).length === 0;
};

export const getBracketUrl = (
	teamLeagueUuid: string,
	poolUuid: string,
	isRoundRobin: boolean,
	teamLeagueTournamentUuid: string | null,
	poolCount: number
) => {
	if (poolCount > 1) {
		return `${publicEnv.NEXT_PUBLIC_PTL_BRACKETS_URL}/team-leagues/${teamLeagueUuid}/tournaments/${teamLeagueTournamentUuid}/overview`;
	}

	const bracketType = isRoundRobin ? 'pools' : 'elimination';

	return `${publicEnv.NEXT_PUBLIC_PTL_BRACKETS_URL}/team-leagues/${teamLeagueUuid}/tournaments/${teamLeagueTournamentUuid}/${bracketType}/${poolUuid}`;
};

export const getTournamentEventTitle = (tournamentEvent: TournamentEvent) => {
	let tournamentTitle = tournamentEvent.title;
	if (tournamentEvent.isRoundRobinBracketType && tournamentEvent.poolSeqNumber === 2) {
		tournamentTitle += ' - Playoffs';
	} else if (tournamentEvent.isRoundRobinBracketType && tournamentEvent.poolSeqNumber === 1) {
		tournamentTitle += ` - ${tournamentEvent.poolTitle}`;
	}
	return tournamentTitle;
};

export const determineSponsorImage = (sponsorImages: SponsorImage[]): SponsorImage | undefined => {
	let xl: SponsorImage | undefined;
	let l: SponsorImage | undefined;
	let m: SponsorImage | undefined;
	let first: SponsorImage | undefined;

	for (const img of sponsorImages) {
		if (!first) first = img;
		if (img.isPrimary) return img;
		if (!xl && img.size === 'XL') xl = img;
		if (!l && img.size === 'L') l = img;
		if (!m && img.size === 'M') m = img;
	}

	return xl ?? l ?? m ?? first;
};

export const formatPlayerLastName = (lastName: string, showOnlyFirstInitialOfLastNameToPublic: boolean) => {
	const permissionStore = usePermissionStore();
	return formatName({ lastName, showLastNameInitialOnly: showOnlyFirstInitialOfLastNameToPublic && !permissionStore.unlimitedAccess });
};

export const formatPlayerMiddleName = (middleName: string, showMiddleNameToPublic: boolean) => {
	const permissionStore = usePermissionStore();
	return showMiddleNameToPublic || permissionStore.unlimitedAccess ? middleName : '';
};

export const getDistrictOptions = (districts: TeamLeagueDistrict[] | undefined, includeAll: boolean = false): Options[] => {
	if (!districts || districts?.length === 0) {
		return [];
	}

	const mappedOptions: Options[] = districts.map((district) => ({
		value: district.uuid,
		label: district.title
	}));

	if (includeAll) {
		return [{ value: 'all', label: 'All' }, ...mappedOptions];
	}

	return mappedOptions;
};

export const parseIds = (ids: string | undefined): string[] => {
	if (!ids) return [];

	const parsed = ids
		.split(',')
		.map((s) => s.trim())
		.filter(Boolean);

	return Array.from(new Set(parsed));
};

export const getLastPathSegment = (path: string) => {
	if (!path) return '';
	const segments = path.split('/').filter((segment: string) => segment !== '');
	if (segments.length === 0 || segments.length === 2) return '/';
	return '/' + segments[2];
};

export const buildQuery = (baseParams: Record<string, string | null>) => {
	const params = new URLSearchParams();

	Object.entries(baseParams).forEach(([key, value]) => {
		if (value) params.set(key, value);
	});

	return `?${params.toString()}`;
};

export const socialMediaUrlValidation = (platformUrl: string, secondaryPlatformUrl: string = '', required = false) => {
	const getUrlVariations = (url: string) => {
		const baseUrl = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
		return [`https://${baseUrl}`, `https://www.${baseUrl}`, baseUrl, `www.${baseUrl}`];
	};

	return z
		.union([
			z.string().superRefine((val, ctx) => {
				const trimmed = val.trim();
				if (trimmed === '') {
					if (required) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: 'URL cannot be empty'
						});
					}
					return;
				}

				const urlToCheck = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
				try {
					new URL(urlToCheck);
				} catch {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: `Must be a valid URL`
					});
					return;
				}

				const inputWithoutProtocol = trimmed.replace(/^https?:\/\//, '');
				const platformVariations = getUrlVariations(platformUrl);
				const secondaryVariations = secondaryPlatformUrl ? getUrlVariations(secondaryPlatformUrl) : [];

				if (
					!(
						platformVariations.some((v) => inputWithoutProtocol.startsWith(v.replace(/^https?:\/\//, ''))) ||
						(secondaryPlatformUrl && secondaryVariations.some((v) => inputWithoutProtocol.startsWith(v.replace(/^https?:\/\//, ''))))
					)
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: `URL must start with ${secondaryPlatformUrl ? platformUrl + ' or ' + secondaryPlatformUrl : platformUrl}`
					});
				}
			}),
			z.literal('')
		])
		.nullable()
		.optional();
};
